import { FC, useCallback, useEffect, useMemo } from "react";
import { FieldValues, useController, useFormContext } from "react-hook-form";

import { VALIDATION } from "../../constants/validation";
import { FieldBaseProps } from "../FieldBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { SubmitGroup } from "../SubmitGroup";
import { ValidityGroup } from "../ValidityGroup";
import { FileEntry } from "./FileEntry";
import { FileUploadBaseProps } from "./FileUpload";
import { Input } from "./Input";

/**
 * The `Field` component properties.
 */
export interface FieldProps extends FieldBaseProps<ReadonlyArray<FileEntry> | null>, FileUploadBaseProps { }

/**
 * A React Hook Form compatible FileUpload component. For more information, see the documentation for
 * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
 */
export const Field: FC<FieldProps> = (props) => {
  const { name, validator, required = false, defaultValue = null, maxSize, maxFiles, "aria-describedby": ariaDescribedBy } = props;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);

  // field is invalid if:

  // (a) required and no files uploaded *

  // (b) a file requires metadata that is not entered

  // (c) a file requires metadata and required metadata fields are not filled.

  // (d) any of the files do not match the maximum size requirement. *

  const validateHandler = useCallback(
    (value: ReadonlyArray<FileEntry> | null, formValues: FieldValues) => {
      if (required && (value === null || value === undefined || value.length === 0)) return VALIDATION.REQUIRED_FILE;

      if (value && maxFiles !== undefined && value.length > maxFiles) return VALIDATION.MAXIMUM_FILES;

      if (value && maxSize !== undefined && value.find((entry) => entry.file && entry.file.size > maxSize))
        return "One or more files are too large to upload.";

      return validator ? validator(value, formValues) : true;
    },
    [required, validator, maxFiles, maxSize],
  );

  const rules = useMemo(
    () => ({
      validate: disabled || readOnly ? undefined : validateHandler,
    }),
    [validateHandler, disabled, readOnly],
  );

  const { resetField } = useFormContext();

  useEffect(() => {
    if (disabled) {
      resetField(name);
    }
  }, [disabled]);

  const { field, fieldState, formState } = useController({
    name,
    defaultValue,
    rules,
  });

  const { ref, value, onChange, onBlur } = field;
  const { invalid, error } = fieldState;
  const { isSubmitting } = formState;

  return (
    <SubmitGroup submitting={isSubmitting}>
      <ValidityGroup invalid={invalid} error={error?.message}>
        <Input
          {...props}
          ref={ref}
          value={value}
          onChange={onChange}
          onFocusExit={onBlur}
          aria-describedby={invalid ? `${name}-error` : ariaDescribedBy}
        />
      </ValidityGroup>
    </SubmitGroup>
  );
};
